import TimeRange from "@/data/models/TimeRange";
import {
  DeviceAttributeInput,
  DeviceAttributeType,
  DevicePowerUsageType,
} from "@/data/models/DeviceAttribute";
import { BaseDeviceInfo } from "@/data/models/BaseDeviceInfo";
import { StringInput } from "@/utils/StringInput";
import { parseNumber } from "@/utils/NumberParser";
import { firestore } from "firebase";
import { RecordInfo } from "./RecordInfo";

export const MAX_QR_CODE_LENGTH = 30;

export enum DeviceType {
  IRES,
  GENERIC,
  OVERVIEW,
}

export interface GenericDevice extends BaseDeviceInfo {
  id: string;
  internalNumber: number;
  templateId: string;
  categoryId: string;
  currentAssignedProjectId?: string | null;
  currentAssignedProjectName?: string | null;
  currentAssignedProjectAssignmentTimeRange?: TimeRange | null;
  lastAssignedProjectId?: string | null;
  lastAssignedProjectName?: string | null;
  lastAssignedProjectAssignmentTimeRange?: TimeRange | null;
  knownSince: firestore.Timestamp;
  selectedPowerConsumption?: DeviceAttributeType | null;
  dateOfLastMeterReading?: string | null;
  lastMeterReading?: number | null;
  flaggedAsDeleted: boolean;
  input: Record<string, string>;
}

export interface NewGenericDevice {
  categoryName: string;
  deviceName: string;
  categoryId: string;
  templateId: string;
  inventoryNumber: string;
  internalNumber: number;
  serialNumber?: string | null;
  manufacturer?: string | null;
  powerConsumptionPerHour?: number | null;
  qrCode?: string | null;
  devicePowerUsageType?: DevicePowerUsageType | null;
  electricityMeterManufacturer?: string | null;
  electricityMeterNumber?: string | null;
  acquisitionDate?: firestore.Timestamp | null;
  selectedPowerConsumption?: DeviceAttributeType | null;
  knownSince?: firestore.Timestamp | null;
  availability?: number;
  /** Branch number the device currently stands in. */
  branch?: string | null;
  /** Branch number the device belongs to. Carried by the CSV's STANDORT column. */
  homeLocation?: string | null;
  note?: string | null;
  lastMeterReading?: number | null;
  vehicleId?: string | null;
}

export interface GenericDeviceOverview {
  id: string;
  branch?: string | null;
  templateId: string;
  categoryId: string;
  inventoryNumber: string;
  isCurrentlyAssigned: boolean;
  projectId?: string | null;
  projectName?: string | null;
  projectAssignmentTimeRange?: TimeRange | null;
  electricityMeterManufacturer?: string | null;
  pageNumber?: number | null;
  electricityMeterNumber?: string | null;
  qrCode?: string | null;
  availability: number;
  internalNumber?: number;
  vehicleId?: string | null;
  deviceLastMoved?: RecordInfo | null; 
}

function generateInputValues(
  inventoryNumber: string,
  electricityMeterManufacturer?: string | null,
  electricityMeterNumber?: string | null,
  note?: string | null,
  powerConsumptionPerHour?: number | null,
) {
  const inputValues: DeviceAttributeInput[] = [];

  inputValues.push({
    deviceAttributeType: DeviceAttributeType.INVENTORY_NUMBER,
    input: inventoryNumber
  });

  if (electricityMeterManufacturer) {
    inputValues.push({
      deviceAttributeType: DeviceAttributeType.ELECTRICITY_METER_MANUFACTURER,
      input: electricityMeterManufacturer
    });
  }

  if (electricityMeterNumber) {
    inputValues.push({
      deviceAttributeType: DeviceAttributeType.ELECTRICITY_METER_NUMBER,
      input: electricityMeterNumber
    });
  }

  if (note?.trim() !== '') {
    inputValues.push({
      deviceAttributeType: DeviceAttributeType.COMMENT,
      input: note
    });
  }

  if (powerConsumptionPerHour && parseNumber(powerConsumptionPerHour.toString())) {
    inputValues.push({
      deviceAttributeType: DeviceAttributeType.POWER_CONSUMPTION_PER_HOUR,
      input: powerConsumptionPerHour.toString()
    });
  }

  return inputValues;
}

export function generateInput(
  inventoryNumber: string,
  electricityMeterManufacturer?: string | null,
  electricityMeterNumber?: string | null,
  note?: string | null,
  powerConsumptionPerHour?: number | null
) {
  const input: Record<string, string> = {};
  const inputValues = generateInputValues(
    inventoryNumber,
    electricityMeterManufacturer,
    electricityMeterNumber,
    note,
    powerConsumptionPerHour
  );

  inputValues.forEach((item: DeviceAttributeInput) => {
    const attribute: DeviceAttributeType = item.deviceAttributeType;

    switch (attribute) {
      case DeviceAttributeType.INVENTORY_NUMBER:
      case DeviceAttributeType.ELECTRICITY_METER_MANUFACTURER:
      case DeviceAttributeType.ELECTRICITY_METER_NUMBER:
        break;
      default:
        if (item.input) {
          input[item.deviceAttributeType.toString()] = JSON.stringify({ input: item.input || "" });
        }
        break;
    }
  });

  return input;
}

export function setStringInput(deviceAttributeType: DeviceAttributeType, value: string): { [key: string]: string } {
  const input: { [key: string]: string } = {};
  input[deviceAttributeType] = JSON.stringify(new StringInput(value));
  return input;
}
